import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ShareNetwork, X, WhatsappLogo, XLogo, FacebookLogo, LinkedinLogo,
  TelegramLogo, Envelope, Link as LinkIcon, DeviceMobile,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * SharePopover — premium social share menu for the auction detail page.
 *
 * Sends a lightweight growth event on every share intent so the admin
 * dashboard can track which channels drive real reach.
 */
export default function SharePopover({ auction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const price = auction.current_price || auction.starting_price || auction.buy_now_price || 0;
  const priceText = `₺${Number(price).toLocaleString("tr-TR")}`;
  const shareText = `${auction.title} — ${priceText} · kolaysatis.com`;
  const encoded = { u: encodeURIComponent(url), t: encodeURIComponent(shareText) };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const track = (channel) => {
    // Best-effort growth event — swallow errors so share never breaks
    api.post("/growth/events", {
      kind: "upsell_shown", // reused funnel bucket; no dedicated table needed
      listing_id: auction.id,
      doping_type: `share_${channel}`,
    }).catch(() => {});
  };

  const openWindow = (u, channel) => {
    track(channel);
    window.open(u, "_blank", "noopener,width=650,height=560");
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopyalandı");
      track("copy_link");
    } catch {
      toast.error("Kopyalama başarısız — tarayıcı izin vermedi");
    }
    setOpen(false);
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: auction.title, text: shareText, url });
      track("native");
    } catch { /* user cancel */ }
    setOpen(false);
  };

  const options = [
    {
      key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo,
      color: "text-emerald-600",
      onClick: () => openWindow(`https://wa.me/?text=${encoded.t}%20${encoded.u}`, "whatsapp"),
    },
    {
      key: "twitter", label: "X (Twitter)", icon: XLogo, color: "text-slate-900",
      onClick: () => openWindow(`https://twitter.com/intent/tweet?text=${encoded.t}&url=${encoded.u}`, "twitter"),
    },
    {
      key: "facebook", label: "Facebook", icon: FacebookLogo, color: "text-blue-700",
      onClick: () => openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encoded.u}`, "facebook"),
    },
    {
      key: "linkedin", label: "LinkedIn", icon: LinkedinLogo, color: "text-sky-700",
      onClick: () => openWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encoded.u}`, "linkedin"),
    },
    {
      key: "telegram", label: "Telegram", icon: TelegramLogo, color: "text-sky-500",
      onClick: () => openWindow(`https://t.me/share/url?url=${encoded.u}&text=${encoded.t}`, "telegram"),
    },
    {
      key: "email", label: "E-posta", icon: Envelope, color: "text-slate-700",
      onClick: () => {
        track("email");
        window.location.href = `mailto:?subject=${encoded.t}&body=${encoded.t}%0A%0A${encoded.u}`;
        setOpen(false);
      },
    },
    {
      key: "copy", label: "Link kopyala", icon: LinkIcon, color: "text-slate-700",
      onClick: copyLink,
    },
  ];

  const hasNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="p-2 rounded-full hover:bg-slate-50 transition-colors"
        aria-label="Paylaş"
        aria-expanded={open}
        data-testid="share-toggle"
      >
        <ShareNetwork size={16} className="text-slate-600" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white rounded-2xl soft-shadow-lg border border-slate-100 overflow-hidden z-40 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid="share-popover"
          role="dialog"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b divider">
            <p className="text-[11px] uppercase tracking-[0.24em] font-medium text-slate-500">
              Paylaş
            </p>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-slate-50"
              aria-label="Kapat"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
          {hasNative && (
            <button
              onClick={nativeShare}
              data-testid="share-native"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b divider"
            >
              <DeviceMobile size={18} weight="regular" className="text-slate-700" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">Cihazla paylaş</p>
                <p className="text-[11px] text-slate-500">Native paylaşım</p>
              </div>
            </button>
          )}
          <div className="grid grid-cols-1 divide-y divider" role="menu">
            {options.map((o) => (
              <button
                key={o.key}
                onClick={o.onClick}
                data-testid={`share-${o.key}`}
                role="menuitem"
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
              >
                <o.icon size={18} weight="fill" className={o.color} />
                <span className="text-sm font-medium text-slate-900 flex-1">{o.label}</span>
              </button>
            ))}
          </div>
          <div className="px-4 py-2.5 bg-slate-50 text-[11px] text-slate-500 leading-relaxed truncate">
            {shareText}
          </div>
        </div>
      )}
    </div>
  );
}
